import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  FilePlus,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ExternalLink,
  ListTree,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from "recharts";
import { PageLoader } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import {
  useContractorLayers,
  useContractorGroupDetail,
} from "@/features/finances/hooks/useGroupForecast";
import { QuickDraftRequisitionModal } from "@/features/finances/components/QuickDraftRequisitionModal";
import type { ContractorLayerRow } from "@/features/finances/types/groupForecast";

type LayerKey = "invoice" | "order" | "approved_req" | "draft";
const LAYER_META: {
  key: LayerKey;
  label: string;
  hint: string;
  color: string;
}[] = [
  {
    key: "invoice",
    label: "Faktura",
    hint: "Zaakceptowana faktura — koszt rzeczywisty",
    color: "#16a34a",
  },
  {
    key: "order",
    label: "Zamówienie",
    hint: "Zakontraktowane zamówienie bez faktury",
    color: "#2563eb",
  },
  {
    key: "approved_req",
    label: "Zatwierdzone",
    hint: "Zatwierdzone zapotrzebowanie bez zamówienia",
    color: "#f59e0b",
  },
  {
    key: "draft",
    label: "Szkic",
    hint: "Szkic zapotrzebowania — szacunek",
    color: "#9ca3af",
  },
];
const COLOR_OF: Record<LayerKey, string> = Object.fromEntries(
  LAYER_META.map((m) => [m.key, m.color]),
) as Record<LayerKey, string>;

const num = (v: string): number => parseFloat(v) || 0;

type SortKey =
  | "group_name"
  | "total_value"
  | "total_contractor"
  | "consumed_pct"
  | LayerKey
  | "project_count";

export function ContractorLayersPage() {
  const { data, isLoading } = useContractorLayers();
  const [draftOpen, setDraftOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "total_contractor",
    dir: "desc",
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const rows = useMemo(() => {
    const r = (data ?? []).slice();
    const q = query.trim().toLowerCase();
    const filtered = q
      ? r.filter(
          (x) =>
            x.group_name.toLowerCase().includes(q) ||
            (x.short_name ?? "").toLowerCase().includes(q),
        )
      : r;
    const val = (x: ContractorLayerRow): number | string => {
      if (sort.key === "group_name") return x.group_name.toLowerCase();
      if (sort.key === "consumed_pct") return x.consumed_pct ?? -1;
      if (sort.key === "project_count") return x.project_count;
      return num(x[sort.key]);
    };
    filtered.sort((a, b) => {
      const va = val(a),
        vb = val(b);
      const cmp =
        typeof va === "string"
          ? va.localeCompare(vb as string)
          : (va as number) - (vb as number);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return filtered;
  }, [data, query, sort]);

  const selected = rows.find((r) => r.group_id === selectedId) ?? null;

  if (isLoading || !data) return <PageLoader />;

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" },
    );
  }
  const SortIcon = ({ k }: { k: SortKey }) =>
    sort.key !== k ? (
      <ChevronsUpDown size={12} className="inline opacity-40" />
    ) : sort.dir === "asc" ? (
      <ChevronUp size={12} className="inline" />
    ) : (
      <ChevronDown size={12} className="inline" />
    );

  const Th = ({
    k,
    children,
    right,
  }: {
    k: SortKey;
    children: React.ReactNode;
    right?: boolean;
  }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-3 font-medium cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${right ? "text-right" : "text-left"}`}
    >
      {children} <SortIcon k={k} />
    </th>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100">
            <Layers size={22} className="text-[var(--color-primary)]" /> Koszty
            podwykonawcze wg statusu
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
            Koszt podwykonawców wg najtwardszego dostępnego źródła
            (faktura → zamówienie → zatwierdzone zapotrzebowanie → szkic), każda
            pozycja ujęta dokładnie raz. Wszystkie kwoty <b>netto</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtruj grupę zleceń…"
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 w-56 bg-white dark:bg-gray-800 dark:text-gray-100"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDraftOpen(true)}
          >
            <FilePlus size={15} className="mr-1.5" /> Szybki szkic
          </Button>
        </div>
      </div>

      {draftOpen && (
        <QuickDraftRequisitionModal onClose={() => setDraftOpen(false)} />
      )}

      <div className="flex flex-col xl:flex-row gap-4 items-start">
        {/* Tabela */}
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <Th k="group_name">Grupa zleceń</Th>
                <Th k="total_value" right>
                  Wartość
                </Th>
                {LAYER_META.map((m) => (
                  <Th key={m.key} k={m.key} right>
                    {m.label}
                  </Th>
                ))}
                <Th k="total_contractor" right>
                  Razem podw. netto
                </Th>
                <Th k="consumed_pct" right>
                  % podzlecenia
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isSel = row.group_id === selectedId;
                const pct = row.consumed_pct;
                return (
                  <tr
                    key={row.group_id ?? "none"}
                    onClick={() => setSelectedId(row.group_id)}
                    className={`border-b border-gray-50 dark:border-gray-700/50 cursor-pointer ${isSel ? "bg-[var(--color-primary)]/5" : "hover:bg-gray-50 dark:hover:bg-gray-700/30"}`}
                  >
                    <td className="px-3 py-3 text-gray-800 dark:text-gray-100">
                      {row.short_name && (
                        <span className="font-mono text-xs text-gray-400 mr-1.5">
                          {row.short_name}
                        </span>
                      )}
                      {row.group_name}
                      <span className="text-xs text-gray-400 ml-1.5">
                        ({row.project_count})
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                      {formatAmount(row.total_value)}
                    </td>
                    {LAYER_META.map((m) => {
                      const v = num(row[m.key]);
                      return (
                        <td
                          key={m.key}
                          className="px-3 py-3 text-right tabular-nums"
                          style={{ color: v > 0 ? m.color : undefined }}
                        >
                          {v > 0 ? (
                            formatAmount(row[m.key])
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">
                              —
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-right tabular-nums font-bold text-gray-900 dark:text-gray-100">
                      {formatAmount(row.total_contractor)}
                    </td>
                    <td
                      className={`px-3 py-3 text-right tabular-nums font-semibold ${pct === null ? "text-gray-300" : pct > 60 ? "text-red-600 dark:text-red-400" : pct > 30 ? "text-amber-600 dark:text-amber-400" : "text-gray-600 dark:text-gray-300"}`}
                    >
                      {pct === null ? "—" : `${pct.toFixed(0)}%`}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-10 text-center text-gray-400"
                  >
                    Brak grup pasujących do filtra.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Panel wybranej grupy */}
        {selected ? (
          <GroupPanel row={selected} onOpenDetail={() => setDetailOpen(true)} />
        ) : (
          <div className="xl:w-80 shrink-0 w-full rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-6 text-center text-sm text-gray-400">
            Kliknij grupę w tabeli, by zobaczyć rozkład kosztów podwykonawczych
            i listę projektów.
          </div>
        )}
      </div>

      {detailOpen && selected && (
        <ContractorDetailModal
          groupId={selected.group_id}
          groupName={selected.group_name}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

function GroupPanel({
  row,
  onOpenDetail,
}: {
  row: ContractorLayerRow;
  onOpenDetail: () => void;
}) {
  const pieData = LAYER_META.map((m) => ({
    key: m.key,
    name: m.label,
    value: num(row[m.key]),
    color: m.color,
  })).filter((d) => d.value > 0);
  const total = num(row.total_contractor);
  const sumValues = row.projects.reduce((s, p) => s + p.value, 0);

  return (
    <div className="xl:w-80 shrink-0 w-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-4">
      <div>
        <h3 className="font-bold text-gray-900 dark:text-gray-100 truncate">
          {row.group_name}
        </h3>
        <p className="text-xs text-gray-500">
          Podwykonawcy (netto):{" "}
          <b className="text-gray-900 dark:text-gray-100">
            {formatAmount(row.total_contractor)} PLN
          </b>
          {row.consumed_pct !== null && (
            <> · {row.consumed_pct.toFixed(0)}% wartości</>
          )}
        </p>
      </div>

      {/* Wykres kołowy warstw (suma = 100%) */}
      {total > 0 ? (
        <>
          <button
            onClick={onOpenDetail}
            className="w-full"
            title="Kliknij, by zobaczyć szczegóły pozycji"
          >
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={70}
                  paddingAngle={2}
                >
                  {pieData.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Pie>
                <ReTooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value, name) => {
                    const v = Number(value) || 0;
                    return [
                      `${formatAmount(v.toFixed(2))} PLN netto (${total > 0 ? Math.round((v / total) * 100) : 0}%)`,
                      name,
                    ];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </button>
          {/* Legenda warstw z kwotami */}
          <div className="space-y-1">
            {LAYER_META.map((m) => {
              const v = num(row[m.key]);
              if (v <= 0) return null;
              return (
                <div
                  key={m.key}
                  className="flex items-center justify-between text-xs"
                  title={m.hint}
                >
                  <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                    <span
                      className="inline-block w-3 h-3 rounded-sm"
                      style={{ background: m.color }}
                    />{" "}
                    {m.label}
                  </span>
                  <span className="tabular-nums text-gray-900 dark:text-gray-100">
                    {formatAmount(row[m.key])}{" "}
                    <span className="text-gray-400">
                      ({Math.round((v / total) * 100)}%)
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenDetail}
            className="w-full"
          >
            <ListTree size={15} className="mr-1.5" /> Szczegóły pozycji
          </Button>
        </>
      ) : (
        <p className="text-xs text-gray-400 text-center py-4">
          Brak kosztów podwykonawczych w tej grupie.
        </p>
      )}

      {/* Lista projektów grupy z wartościami */}
      <div>
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-gray-400 mb-1">
          <span>Projekty ({row.projects.length})</span>
          <span>wartość</span>
        </div>
        <div className="rounded-lg border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50 max-h-52 overflow-y-auto">
          {row.projects.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs"
            >
              <span className="truncate text-gray-700 dark:text-gray-200">
                {p.short_name && (
                  <span className="font-mono text-gray-400 mr-1">
                    {p.short_name}
                  </span>
                )}
                {p.name}
              </span>
              <span className="tabular-nums text-gray-900 dark:text-gray-100 shrink-0">
                {formatAmount(p.value.toFixed(2))}
              </span>
            </div>
          ))}
          {row.projects.length === 0 && (
            <div className="px-2.5 py-3 text-center text-gray-400 text-xs">
              Brak projektów.
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-2.5 pt-2 text-xs font-semibold text-gray-900 dark:text-gray-100">
          <span>Suma wartości</span>
          <span className="tabular-nums">
            {formatAmount(sumValues.toFixed(2))} PLN
          </span>
        </div>
      </div>
    </div>
  );
}

function ContractorDetailModal({
  groupId,
  groupName,
  onClose,
}: {
  groupId: string | null;
  groupName: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useContractorGroupDetail(groupId);
  // sort wg "starszości" statusu (faktura → zamówienie → zatwierdzone → szkic), potem po nazwie
  const order: Record<LayerKey, number> = { invoice: 0, order: 1, approved_req: 2, draft: 3 };
  const items = [...(data?.items ?? [])].sort(
    (a, b) => (order[a.layer] - order[b.layer]) || a.name.localeCompare(b.name),
  );
  const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[52rem] max-w-full max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
              {groupName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pozycje podwykonawcze — faktury, zamówienia, zapotrzebowania. Kwoty netto.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto">
          {isLoading ? (
            <div className="py-16 text-center text-sm text-gray-400">
              Ładowanie…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">
              Brak pozycji podwykonawczych.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-2.5 font-medium w-32">Status</th>
                  <th className="px-4 py-2.5 font-medium">Pozycja</th>
                  <th className="px-4 py-2.5 font-medium text-right w-40">
                    Kwota netto
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-sm"
                          style={{ background: COLOR_OF[it.layer] }}
                        />{" "}
                        {it.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Link
                        to={it.url}
                        className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                        onClick={onClose}
                      >
                        {it.name}{" "}
                        <ExternalLink size={12} className="opacity-60" />
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                      {formatAmount(it.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 font-bold text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3" colSpan={2}>
                    Razem ({items.length})
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatAmount(total.toFixed(2))} PLN
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
